(() => {
  'use strict';

  // How many stops a slider offers between its ends. The thumbs move on this
  // even grid rather than hopping between the actual values in the collection:
  // mileage has 100 distinct values spread over 100..120000, so snapping to
  // them made a pixel of drag jump thousands of km (Jiri: "skočí z 35k na 19k").
  const TARGET_STEPS = 100;

  // Measured against the live storefront: 300 values (10.4 kB of URL) returns
  // 200, 500 values (17.4 kB) returns 414. Left short of the real ceiling so
  // other active filters have room in the query string.
  const MAX_URL_VALUES = 300;

  // Half the visible thumb circle, mirrored from custom.css.liquid, where the
  // slider is pulled 5px outside the rail so a thumb at either end sits flush
  // with it. The rail fill has to follow the thumb centres by the same amount,
  // otherwise the coloured segment and the thumbs drift apart at the extremes.
  const EDGE_INSET = 10;

  const debounce = (fn, wait) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  // Format number: remove trailing zeros in decimal, add space as thousand separator.
  // Strips existing separators first so the result is idempotent — parseFloat on
  // an already-grouped "8 500" would otherwise stop at the space and yield 8.
  // `group` is off for filters that never reach five figures, so the Year filter
  // reads "2024" rather than "2 024".
  const formatNumber = (n, group = true) => {
    const num = parseFloat(String(n).replace(/\s/g, ''));
    if (isNaN(num)) return String(n);
    const isWhole = Number.isInteger(num) || Math.abs(num - Math.round(num)) < 1e-9;
    const str = isWhole ? String(Math.round(num)) : String(num);
    const [intPart, decPart] = str.split('.');
    const grouped = group ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : intPart;
    return decPart !== undefined ? `${grouped}.${decPart}` : grouped;
  };

  // Whether an element sits inside a filter that opts out of thousand grouping.
  // Liquid marks the container, so a chip and its slider always agree.
  const groupingFor = (el) =>
    typeof el.closest === 'function' ? !el.closest('[data-nrf-group="false"]') : true;

  // Format all elements marked with .format-number (chips, displays, etc.)
  // Handles both regular elements (textContent) and inputs (value).
  const formatStaticElements = (root = document) => {
    root.querySelectorAll('.format-number').forEach(el => {
      if (el.dataset.formatted === '1') return;
      el.dataset.formatted = '1';
      const isInput = el.tagName === 'INPUT';
      const raw = (isInput ? el.value : el.textContent).trim();
      const formatted = formatNumber(raw, groupingFor(el));
      if (isInput) el.value = formatted;
      else el.textContent = formatted;
    });
  };

  // A round step of 1, 2 or 5 x 10^n, so the numbers under the thumb read like
  // something a person would say (1 000 km, not 1 199 km).
  const niceStep = (span, integersOnly) => {
    if (!(span > 0)) return 0;
    const raw = span / TARGET_STEPS;
    const mag = Math.pow(10, Math.floor(Math.log10(raw)));
    const norm = raw / mag;
    // Round the step down, so the grid ends up at or above TARGET_STEPS rather
    // than half of it.
    const mult = norm >= 5 ? 5 : norm >= 2 ? 2 : 1;
    const step = mult * mag;
    return integersOnly ? Math.max(1, Math.round(step)) : step;
  };

  // Hand the navigation to the theme's own AJAX filter pipeline when it is
  // there, so a range behaves like every other filter — no full reload, and the
  // mobile filter drawer stays open. Falls back to a plain navigation.
  const navigate = (url) => {
    const theme = window.BlsEventCollectionShopify;
    const section = document.querySelector('.section-collection-product');
    if (theme && typeof theme.renderSectionFilter === 'function' && section) {
      const searchParams = url.searchParams.toString();
      theme.renderSectionFilter(theme.renderUrl(searchParams), searchParams);
      return;
    }
    window.location.href = url.toString();
  };

  class NumericRangeFilter {
    constructor(container) {
      this.container = container;
      this.paramName = container.dataset.paramName;

      // Shopify's own value strings, kept verbatim — they are what has to go
      // back into the filter URL. Parsing them to numbers and re-serialising
      // loses the exact encoding (a number_decimal "13100.0" comes back out as
      // "13100"), Shopify then does not recognise the value and drops it, and
      // the collection renders unfiltered. Numbers are only ever used for the
      // slider maths; `raw` is what gets appended to the URL.
      // Shopify caps filter.values at 100 but still matches values it never
      // returned, so anything past the cap is unreachable purely because we
      // were never told it exists. data-extra-values carries the full set
      // (see shop.metafields.filter_values.<key> in collection-sidebar.liquid)
      // and is merged in here. Absent, this is a no-op.
      const declared = JSON.parse(container.dataset.values || '[]');
      const extra = JSON.parse(container.dataset.extraValues || '[]');
      const byRaw = new Map();
      [].concat(declared, extra).forEach(v => {
        const raw = String(v);
        const num = parseFloat(v);
        if (!isNaN(num) && !byRaw.has(raw)) byRaw.set(raw, { raw, num });
      });
      this.entries = [...byRaw.values()].sort((a, b) => a.num - b.num);
      // Kept so applyFilter can fall back to the set Shopify vouched for if the
      // full one ever stops fitting in a URL.
      this.declared = new Set(declared.map(v => String(v)));

      this.dataMin = parseFloat(container.dataset.min);
      this.dataMax = parseFloat(container.dataset.max);

      // Liquid can only rebuild the slider from filter.active_values — the
      // values that actually matched — so a 90..95 kWh window holding one car
      // comes back as "93.7 - 93.7" and the thumbs jump on every reload. The
      // window the visitor actually chose is carried in its own parameter;
      // Shopify ignores it, and it is the only record of their intent.
      this.windowParam = `nrf.${this.paramName}`;

      this.minSlider = container.querySelector('.nrf-slider-min');
      this.maxSlider = container.querySelector('.nrf-slider-max');
      this.minInput = container.querySelector('.nrf-input-min');
      this.maxInput = container.querySelector('.nrf-input-max');
      this.minDisplay = container.querySelector('.nrf-min-display');
      this.maxDisplay = container.querySelector('.nrf-max-display');
      this.track = container.querySelector('.nrf-track');
      if (!this.minSlider || !this.maxSlider || !this.track) return;

      const span = this.dataMax - this.dataMin;
      const integersOnly = this.entries.every(e => Number.isInteger(e.num));
      this.group = this.dataMax >= 10000;
      this.step = niceStep(span, integersOnly);
      // Published so the grid is inspectable from the console and assertable
      // from .claude/filter-tests.js.
      container.dataset.nrfStep = String(this.step);
      // Keeping the thumbs at least one step apart means they can never land on
      // the same pixel, where the lower one becomes impossible to grab.
      this.gap = span > 0 ? Math.min(this.step, span) : 0;

      this.debouncedApply = debounce(() => this.applyFilter(), 400);
      this.bindEvents();
      this.restoreWindow();
      this.update();
      this.syncChip();
    }

    // Put the thumbs back where the visitor left them, rather than on the
    // extent of whatever matched.
    restoreWindow() {
      const raw = new URLSearchParams(window.location.search).get(this.windowParam);
      if (!raw) return;
      const [min, max] = raw.split(':').map(v => parseFloat(v));
      if (!isFinite(min) || !isFinite(max) || min > max) return;
      if (max < this.dataMin || min > this.dataMax) return;
      this.minSlider.value = Math.max(this.dataMin, min);
      this.maxSlider.value = Math.min(this.dataMax, max);
    }

    // The chip is rendered from the matched values too, so it needs the same
    // correction — otherwise the sidebar reads "90 - 95" and the chip "93.7".
    syncChip() {
      const chip = document.querySelector(`[data-nrf-clear="${this.paramName}"]`);
      if (!chip) return;
      const spans = chip.querySelectorAll('.format-number');
      if (spans.length !== 2) return;
      const values = [this.minSlider.value, this.maxSlider.value];
      spans.forEach((span, i) => {
        span.textContent = formatNumber(values[i], this.group);
        span.dataset.formatted = '1';
      });
    }

    bindEvents() {
      this.minSlider.addEventListener('input', () => this.update('min'));
      this.maxSlider.addEventListener('input', () => this.update('max'));
      // Debounced rather than immediate: a range input fires `change` on every
      // arrow keypress, which would otherwise be one request per keystroke.
      this.minSlider.addEventListener('change', () => this.debouncedApply());
      this.maxSlider.addEventListener('change', () => this.debouncedApply());
      this.minInput.addEventListener('change', () => this.onInputChange());
      this.maxInput.addEventListener('change', () => this.onInputChange());
    }

    // Pull a raw slider position onto the even step grid. Both ends stay exactly
    // reachable — the last step before dataMax is usually a partial one, and
    // dataMax has to be hittable or the filter could never be cleared again.
    quantize(v) {
      if (!(v > this.dataMin)) return this.dataMin;
      if (v >= this.dataMax || !this.step) return this.dataMax;
      // A value straight from the data set is always a valid stop — expand()
      // lands on one, and rounding it onto the grid would label a 93.7 kWh car
      // as 93.8.
      if (this.entries.some(e => e.num === v)) return v;
      // Anchored at zero rather than at dataMin, so the stops are round numbers
      // (40 000 km, not 40 100) and land on the values the data actually uses —
      // otherwise the slider label and the applied-range chip disagree.
      const q = Math.round(v / this.step) * this.step;
      if (q <= this.dataMin) return this.dataMin;
      if (q >= this.dataMax) return this.dataMax;
      return parseFloat(q.toFixed(6));
    }

    // The values the window encloses — the set the filter asks Shopify for.
    // Shopify has no range operator for metafields (filter.p.m.*.gte is
    // accepted and then ignored; only filter.v.price supports it), so a range
    // has to be expressed as every value inside it.
    selection(min, max) {
      return this.entries.filter(e => e.num >= min && e.num <= max);
    }

    // A window can fall between two values — battery capacity jumps 73.6 -> 93.7,
    // so 75..90 encloses nothing. Widen to the nearest real value rather than
    // applying nothing (which silently shows everything), but move ONLY the
    // thumb that was last dragged: widening both ends yanked back a thumb the
    // visitor had deliberately placed and never touched again.
    expand(min, max) {
      if (!this.entries.length || this.selection(min, max).length) return [min, max];
      const below = [...this.entries].reverse().find(e => e.num <= max);
      const above = this.entries.find(e => e.num >= min);
      if (this.lastSource === 'min') {
        if (below) return [below.num, max];
        if (above) return [min, above.num];
      } else {
        // Default (max dragged, or values typed): keep the lower bound.
        if (above) return [min, above.num];
        if (below) return [below.num, max];
      }
      return [min, max];
    }

    onInputChange() {
      // \s already covers the non-breaking space formatNumber may have written;
      // the comma swap lets a Czech keyboard type "13,5" without it parsing as 13.
      let min = parseFloat(this.minInput.value.replace(/\s/g, '').replace(',', '.'));
      let max = parseFloat(this.maxInput.value.replace(/\s/g, '').replace(',', '.'));
      if (isNaN(min)) min = this.dataMin;
      if (isNaN(max)) max = this.dataMax;
      this.minSlider.value = Math.max(this.dataMin, Math.min(this.dataMax, min));
      this.maxSlider.value = Math.max(this.dataMin, Math.min(this.dataMax, max));
      this.update();
      this.debouncedApply();
    }

    // `source` names the thumb being dragged, so it can be stopped short of the
    // other one instead of passing it. Swapping mid-drag would fight the native
    // drag, which keeps writing the pointer position straight back.
    update(source) {
      // Remembered so expand() knows which thumb the visitor is holding, and
      // leaves the other one where they put it.
      if (source) this.lastSource = source;

      let min = this.quantize(parseFloat(this.minSlider.value));
      let max = this.quantize(parseFloat(this.maxSlider.value));

      if (source === 'min') {
        min = Math.min(min, max - this.gap);
        if (min < this.dataMin) {
          min = this.dataMin;
          max = Math.min(this.dataMax, min + this.gap);
        }
      } else if (source === 'max') {
        max = Math.max(max, min + this.gap);
        if (max > this.dataMax) {
          max = this.dataMax;
          min = Math.max(this.dataMin, max - this.gap);
        }
      } else {
        if (min > max) [min, max] = [max, min];
        if (max - min < this.gap) max = Math.min(this.dataMax, min + this.gap);
        if (max - min < this.gap) min = Math.max(this.dataMin, max - this.gap);
      }

      // Always write back, so the sliders, the inputs, the label and the value
      // that gets applied can never drift apart.
      this.minSlider.value = min;
      this.maxSlider.value = max;

      this.minInput.value = formatNumber(min, this.group);
      this.maxInput.value = formatNumber(max, this.group);
      if (this.minDisplay) this.minDisplay.textContent = formatNumber(min, this.group);
      if (this.maxDisplay) this.maxDisplay.textContent = formatNumber(max, this.group);

      const span = this.dataMax - this.dataMin || 1;
      const fromPct = ((min - this.dataMin) / span) * 100;
      const toPct = ((max - this.dataMin) / span) * 100;
      // pct of the track *between* the thumb centres, which sit half a thumb in
      // from each end — see THUMB.
      const railPos = (pct) => {
        const offset = EDGE_INSET - (pct * EDGE_INSET * 2) / 100;
        return `calc(${pct.toFixed(2)}% ${offset < 0 ? '-' : '+'} ${Math.abs(offset).toFixed(2)}px)`;
      };
      this.track.style.setProperty('--range-from', railPos(fromPct));
      this.track.style.setProperty('--range-to', railPos(toPct));

      // The two sliders overlap across the whole track, so where the thumbs sit
      // close together only the upper one can be grabbed. Hand priority to the
      // one that still has somewhere to go, so no position is a dead end.
      const minOnTop = toPct - fromPct < 10 && fromPct > 50;
      this.minSlider.style.zIndex = minOnTop ? '5' : '3';
      this.maxSlider.style.zIndex = minOnTop ? '3' : '5';
    }

    applyFilter() {
      const [min, max] = this.expand(
        this.quantize(parseFloat(this.minSlider.value)),
        this.quantize(parseFloat(this.maxSlider.value))
      );
      // expand() can move the thumbs; keep the UI showing what was applied.
      this.minSlider.value = min;
      this.maxSlider.value = max;
      this.update();

      const url = new URL(window.location.href);
      url.searchParams.delete(this.paramName);
      url.searchParams.delete(this.windowParam);
      // A narrower filter can have fewer pages than we are currently on.
      url.searchParams.delete('page');

      const isFullRange = min <= this.dataMin && max >= this.dataMax;
      if (!isFullRange) {
        let selected = this.selection(min, max);
        // A range is expressed as one URL parameter per enclosed value, and the
        // storefront answers 414 past roughly 400 of them (300 measured fine at
        // 10.4 kB, 500 failed). Rather than emit a URL that breaks the page,
        // fall back to the values Shopify itself returned, which always fit.
        if (selected.length > MAX_URL_VALUES) {
          const vouched = selected.filter(e => this.declared.has(e.raw));
          console.warn(
            `[numeric-range-filter] ${this.paramName}: ${selected.length} values in range exceeds the ` +
            `${MAX_URL_VALUES} that fit in a URL — falling back to the ${vouched.length} Shopify returned. ` +
            `Products in range may be missing; the value set has outgrown URL filtering.`
          );
          selected = vouched.length ? vouched : selected.slice(0, MAX_URL_VALUES);
        }
        selected.forEach(e => url.searchParams.append(this.paramName, e.raw));
        url.searchParams.set(this.windowParam, `${min}:${max}`);
      }

      navigate(url);
    }
  }

  const initAll = () => {
    document.querySelectorAll('.numeric-range-filter').forEach(container => {
      if (container.dataset.numericRangeBound === '1') return;
      container.dataset.numericRangeBound = '1';
      new NumericRangeFilter(container);
    });
  };

  const init = () => {
    initAll();
    formatStaticElements();
  };

  // A range applies as one URL param per enclosed value, so the "active filters"
  // row would otherwise show up to 100 chips for a single drag. The sidebar
  // renders one range chip instead, and clearing it means dropping every
  // instance of that param — which only the client knows how to do.
  document.addEventListener('click', (event) => {
    const chip = event.target.closest('[data-nrf-clear]');
    if (!chip) return;
    event.preventDefault();
    const url = new URL(window.location.href);
    url.searchParams.delete(chip.dataset.nrfClear);
    url.searchParams.delete(`nrf.${chip.dataset.nrfClear}`);
    url.searchParams.delete('page');
    navigate(url);
  });

  // Shopify builds every other filter's href itself and drops parameters it
  // does not recognise, so clicking Brand would lose the chosen window and let
  // the thumbs snap back to whatever matched. Capture phase, so the href is
  // already corrected by the time collection.js reads it in its own handler.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link || link.dataset.nrfClear) return;

    const carried = [...new URLSearchParams(window.location.search)]
      .filter(([key]) => key.startsWith('nrf.'));
    if (!carried.length) return;

    let target;
    try {
      target = new URL(link.getAttribute('href'), window.location.href);
    } catch (err) {
      return;
    }
    // Same collection only; "Clear all" and off-site links keep their own URL.
    if (target.pathname !== window.location.pathname) return;

    let changed = false;
    carried.forEach(([key, value]) => {
      // Only worth carrying while that filter is still applied in the target.
      if (target.searchParams.has(key.slice(4)) && !target.searchParams.has(key)) {
        target.searchParams.set(key, value);
        changed = true;
      }
    });
    if (changed) link.setAttribute('href', target.pathname + target.search);
  }, true);

  // The theme filters by replacing .section-collection-product wholesale
  // (collection.js renderSectionFilter) and re-running only its own init. That
  // event is invisible from here and shopify:section:load never fires outside
  // the editor, so without this observer every slider goes inert as soon as any
  // other filter — the price range included — is touched.
  let scheduled;
  const observer = new MutationObserver(() => {
    clearTimeout(scheduled);
    scheduled = setTimeout(() => {
      observer.disconnect();
      try {
        init();
      } finally {
        observer.observe(document.documentElement, { childList: true, subtree: true });
      }
    }, 50);
  });

  const start = () => {
    init();
    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', init);
})();
